/**
 * Capture mode service for auto-saving content blocks to Firestore
 *
 * This service handles saving audio/image/PDF uploads directly to Firestore
 * in capture mode WITHOUT triggering the LLM. Content is persisted for later
 * retrieval when the user actually submits the chat.
 *
 * Audio messages are written directly to Firestore (no backend API needed).
 */

import { doc, setDoc, deleteDoc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { db } from "@/lib/firebase";
import { getIdentifierConfig } from "@/lib/identifier-config";

export interface CreateCaptureContentRequest {
  type: "image" | "audio" | "file";
  file_name: string;
  relative_gcs_path: string;
  mime_type: string;
  thumbnail?: string;
  metadata?: {
    name?: string;
    size?: number;
    duration?: number;
    downloadUrl?: string;
    /** Photo date from EXIF metadata (ISO 8601 string) - only set when EXIF data available */
    photoDate?: string;
    /** Upload timestamp (ISO 8601 string) - always set */
    uploadDate?: string;
    [key: string]: unknown;
  };
}

export interface CreateCaptureContentResponse {
  status: string;
  message_id: string;
}

export interface AppendContentResponse {
  status: string;
  message_id: string;
  total_blocks: number;
}

/**
 * Delete a capture mode message from Firestore
 *
 * Used when a user deletes an audio/image/PDF from the UI before submitting.
 * Removes the corresponding message to keep Firestore state consistent.
 *
 * Deletes directly from Firestore (no backend API needed).
 *
 * @param userId - User ID (Firebase Auth UID)
 * @param conversationId - Project/conversation ID
 * @param messageId - ID of the message to delete
 */
export async function deleteCaptureMessage(
  userId: string,
  conversationId: string,
  messageId: string
): Promise<void> {
  console.log(`[CaptureService] Deleting message ${messageId} from Firestore`);

  const config = getIdentifierConfig();
  const database = db();

  const messageRef = doc(
    database,
    "users",
    userId,
    config.collectionName,
    conversationId,
    "messages",
    messageId
  );

  await deleteDoc(messageRef);

  console.log(`[CaptureService] ✅ Deleted: message_id=${messageId}`);
}

/**
 * Content block for appending to existing message
 * Supports text, document (audio transcript), and other types
 */
export interface AppendContentBlock {
  type: "text" | "document" | "image";
  text?: string; // For text blocks
  source?: {
    type: "text";
    media_type?: string;
    data?: string; // Transcript text for audio (empty initially, filled by backend)
  };
  title?: string; // For document blocks
  citations?: { enabled: boolean }; // Required for document blocks (Anthropic requirement)
  metadata?: {
    audio?: {
      gcs_path: string; // GCS path for audio playback
      mime_type?: string;
    };
    image_ref?: string; // Reference to associated image
  };
}

/**
 * Append content blocks to an existing message in capture mode
 *
 * Used when user adds text/audio to a message that already has images.
 * New blocks are inserted BEFORE any image blocks to maintain proper order:
 * [audio descriptions] → [text descriptions] → [image]
 *
 * Updates directly in Firestore (no backend API needed).
 *
 * @param userId - User ID (Firebase Auth UID)
 * @param conversationId - Project/conversation ID
 * @param messageId - ID of the existing message to append to
 * @param contentBlocks - Array of content blocks to append (text, document with audio)
 * @returns Response with total block count
 */
export async function appendCaptureContent(
  userId: string,
  conversationId: string,
  messageId: string,
  contentBlocks: AppendContentBlock[]
): Promise<AppendContentResponse> {
  console.log(
    `[CaptureService] Inserting ${contentBlocks.length} content blocks before image in message ${messageId}`
  );

  const config = getIdentifierConfig();
  const database = db();

  const messageRef = doc(
    database,
    "users",
    userId,
    config.collectionName,
    conversationId,
    "messages",
    messageId
  );

  // Read current content to insert at correct position
  const docSnap = await getDoc(messageRef);
  const currentContent = (docSnap.data()?.content as Record<string, unknown>[]) || [];

  // Find the first image block index
  const imageIndex = currentContent.findIndex(
    (block) => block.type === "image"
  );

  // Insert new blocks BEFORE the image (or at end if no image found)
  const newContent =
    imageIndex >= 0
      ? [
          ...currentContent.slice(0, imageIndex),
          ...contentBlocks,
          ...currentContent.slice(imageIndex),
        ]
      : [...currentContent, ...contentBlocks];

  await updateDoc(messageRef, { content: newContent });

  console.log(
    `[CaptureService] ✅ Inserted ${contentBlocks.length} blocks before image in message ${messageId}`
  );
  return { status: "ok", message_id: messageId, total_blocks: newContent.length };
}

/**
 * Create a user message immediately after file upload to Firebase Storage
 *
 * Called right after a file (image, audio, PDF) is uploaded to Firebase Storage
 * in capture mode. Creates a user message with the file reference, ensuring no
 * data loss if the user closes the browser before explicitly saving.
 *
 * Writes directly to Firestore (no backend API needed).
 *
 * @param userId - User ID (Firebase Auth UID)
 * @param conversationId - Project/conversation ID
 * @param request - File details including type, thumbnail (for images), metadata
 * @returns Response with message ID
 */
export async function createCaptureContentMessage(
  userId: string,
  conversationId: string,
  request: CreateCaptureContentRequest,
  preGeneratedMessageId?: string  // Optional pre-generated ID (from use-file-upload.tsx)
): Promise<CreateCaptureContentResponse> {
  console.log(`[CaptureService] Creating ${request.type} message for ${request.file_name}`);

  const config = getIdentifierConfig();
  const database = db();
  // Use pre-generated ID if provided, otherwise generate new UUID
  const messageId = preGeneratedMessageId || uuidv4();

  const messagesRef = doc(
    database,
    "users",
    userId,
    config.collectionName,
    conversationId,
    "messages",
    messageId
  );

  // Build content block based on type
  let contentBlock;

  if (request.type === "audio") {
    // Audio uses document format for transcription
    // Only include duration if it's defined (Firestore doesn't accept undefined)
    const audioMetadata: Record<string, unknown> = {
      gcs_path: request.relative_gcs_path,
      mime_type: request.mime_type,
      fileName: request.file_name,
    };
    if (request.metadata?.duration !== undefined) {
      audioMetadata.duration = request.metadata.duration;
    }

    contentBlock = {
      type: "document",
      source: {
        type: "text",
        media_type: "text/plain",
        data: "", // Empty - transcript added later by backend
      },
      title: `Audio transcript: ${request.file_name}`,
      citations: { enabled: true },
      metadata: {
        audio: audioMetadata,
      },
    };
  } else if (request.type === "image") {
    // Image format with source.relative_gcs_path and metadata
    // Build metadata object, only including defined values (Firestore doesn't accept undefined)
    const uploadDate = new Date().toISOString();

    console.log("[CaptureService] Saving image metadata:", {
      receivedPhotoDate: request.metadata?.photoDate,
      uploadDate,
      fileName: request.file_name,
    });

    const imageMetadata: Record<string, unknown> = {
      name: request.metadata?.name || request.file_name,
      storagePath: request.relative_gcs_path,
      // Always set uploadDate
      uploadDate,
    };
    // Only set photoDate if we have EXIF data
    if (request.metadata?.photoDate) {
      imageMetadata.photoDate = request.metadata.photoDate;
    }
    if (request.metadata?.size !== undefined) {
      imageMetadata.size = request.metadata.size;
    }
    if (request.thumbnail !== undefined) {
      imageMetadata.thumbnail = request.thumbnail;
    }

    contentBlock = {
      type: "image",
      source: {
        type: "storage",
        media_type: request.mime_type,
        relative_gcs_path: request.relative_gcs_path,
      },
      metadata: imageMetadata,
    };
  } else {
    // File/PDF format
    // Build metadata object, only including defined values (Firestore doesn't accept undefined)
    const fileMetadata: Record<string, unknown> = {
      name: request.metadata?.name || request.file_name,
      storagePath: request.relative_gcs_path,
    };
    if (request.metadata?.size !== undefined) {
      fileMetadata.size = request.metadata.size;
    }
    if (request.thumbnail !== undefined) {
      fileMetadata.thumbnail = request.thumbnail;
    }

    contentBlock = {
      type: "document",
      source: {
        type: "storage",
        media_type: request.mime_type,
        relative_gcs_path: request.relative_gcs_path,
      },
      metadata: fileMetadata,
    };
  }

  await setDoc(messagesRef, {
    id: messageId,
    role: "user",
    content: [contentBlock],
    created_at: serverTimestamp(),
  });

  console.log(`[CaptureService] ✅ Created ${request.type} message: message_id=${messageId}`);
  return { status: "ok", message_id: messageId };
}
